'use client';

import { useTranslation } from 'react-i18next';

type ProcessorStats = { processorName: string; attempts: number; successes: number };

export type ApprovalCycle = {
    withRetries: { attempts: number; successes: number; byProcessor: { [processorId: string]: ProcessorStats } };
    firstAttemptOnly: { attempts: number; successes: number };
};

type MitKindCounts = {
    subscriptionRebill: { payments: number };
    apiRecurring: { payments: number };
    unscheduledCof: { payments: number };
};

function rate(attempts: number, successes: number): number {
    return attempts > 0 ? (successes / attempts) * 100 : 0;
}

function foldCycles(
    cycles: Record<string, ApprovalCycle> | undefined,
    totals: { attempts: number; successes: number; firstAttempts: number; firstSuccesses: number },
    processors: Record<string, { name: string; attempts: number; successes: number }>,
) {
    for (const cycle of Object.values(cycles ?? {})) {
        totals.attempts += cycle.withRetries.attempts;
        totals.successes += cycle.withRetries.successes;
        totals.firstAttempts += cycle.firstAttemptOnly.attempts;
        totals.firstSuccesses += cycle.firstAttemptOnly.successes;
        for (const [processorId, processor] of Object.entries(cycle.withRetries.byProcessor)) {
            if (!processors[processorId]) {
                processors[processorId] = { name: processor.processorName, attempts: 0, successes: 0 };
            }
            processors[processorId].attempts += processor.attempts;
            processors[processorId].successes += processor.successes;
        }
    }
}

export function foldMitHeadline(data: {
    mit: Record<string, ApprovalCycle>;
    apiRecurring?: Record<string, ApprovalCycle>;
    unscheduledCof?: ApprovalCycle;
}) {
    const totals = { attempts: 0, successes: 0, firstAttempts: 0, firstSuccesses: 0 };
    const processors: Record<string, { name: string; attempts: number; successes: number }> = {};
    foldCycles(data.mit, totals, processors);
    foldCycles(data.apiRecurring, totals, processors);
    if (data.unscheduledCof) {
        foldCycles({ all: data.unscheduledCof }, totals, processors);
    }
    const processorAggregates = Object.entries(processors)
        .map(([id, processor]) => ({
            id,
            name: processor.name,
            attempts: processor.attempts,
            successes: processor.successes,
            rate: rate(processor.attempts, processor.successes),
        }))
        .sort((a, b) => b.rate - a.rate);
    return {
        primary: {
            overallRate: rate(totals.attempts, totals.successes),
            totalAttempts: totals.attempts,
            totalSuccesses: totals.successes,
            bestProcessor: processorAggregates[0] || null,
            processorAggregates,
        },
        firstAttempt: {
            overallRate: rate(totals.firstAttempts, totals.firstSuccesses),
            totalAttempts: totals.firstAttempts,
            totalSuccesses: totals.firstSuccesses,
            bestProcessor: null,
            processorAggregates: [] as typeof processorAggregates,
        },
    };
}

function KindCard({ title, detail, payments, attempts, successes }: {
    title: string;
    detail?: string;
    payments: number;
    attempts: number;
    successes: number;
}) {
    const { t } = useTranslation('analytics');
    return (
        <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-sm font-medium text-foreground">{title}</p>
            {detail ? <p className="mt-1 text-xs text-muted-foreground">{detail}</p> : null}
            <p className="mt-3 text-2xl font-semibold text-foreground">{rate(attempts, successes).toFixed(1)}%</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('approvalRates.kindPayments', { payments: payments.toLocaleString() })}</p>
            <p className="text-sm text-muted-foreground">{t('approvalRates.kindRate', { rate: rate(attempts, successes).toFixed(1) })}</p>
        </div>
    );
}

function cycleTotals(cycles: Record<string, ApprovalCycle> | undefined) {
    let attempts = 0;
    let successes = 0;
    for (const cycle of Object.values(cycles ?? {})) {
        attempts += cycle.withRetries.attempts;
        successes += cycle.withRetries.successes;
    }
    return { attempts, successes };
}

export function MitKindSummary({ mit, apiRecurring, unscheduledCof, kinds }: {
    mit: Record<string, ApprovalCycle>;
    apiRecurring?: Record<string, ApprovalCycle>;
    unscheduledCof?: ApprovalCycle;
    kinds?: MitKindCounts;
}) {
    const { t } = useTranslation('analytics');
    if (!kinds) return null;
    const subscription = cycleTotals(mit);
    const recurring = cycleTotals(apiRecurring);
    const unscheduled = unscheduledCof?.withRetries ?? { attempts: 0, successes: 0 };
    return (
        <div className="space-y-3">
            <div>
                <p className="text-sm font-semibold text-foreground">{t('approvalRates.kindsTitle')}</p>
                <p className="text-sm text-muted-foreground">{t('approvalRates.kindsDescription')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KindCard
                    title={t('approvalRates.subscriptionRebill')}
                    payments={kinds.subscriptionRebill.payments}
                    attempts={subscription.attempts}
                    successes={subscription.successes}
                />
                <KindCard
                    title={t('approvalRates.apiRecurring')}
                    detail={t('approvalRates.apiRecurringEstimated')}
                    payments={kinds.apiRecurring.payments}
                    attempts={recurring.attempts}
                    successes={recurring.successes}
                />
                <KindCard
                    title={t('approvalRates.unscheduledCof')}
                    payments={kinds.unscheduledCof.payments}
                    attempts={unscheduled.attempts}
                    successes={unscheduled.successes}
                />
            </div>
        </div>
    );
}
